// client/src/pages/Wishlist.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Heart, HeartOff, ShoppingCart, ArrowLeft,
  CheckCircle, XCircle, Trash2, Star, Package, ShoppingBag,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/ConfirmationModal';
import storeService, { WishlistItem } from '@/api/services/storeService';

// ==================== HELPERS ====================

const formatCurrency = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// ==================== SUB-COMPONENTS ====================

const StarRating = ({ rating }: { rating: number }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < full
              ? 'text-amber-400 fill-amber-400'
              : i === full && half
              ? 'text-amber-400 fill-amber-200'
              : 'text-gray-300 dark:text-gray-600'
          }`}
        />
      ))}
      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{rating.toFixed(1)}</span>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const Wishlist = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();


  // ── State ────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<WishlistItem[] | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [clearingAll, setClearingAll] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    mode: 'remove' | 'clear';
    itemId: string | null;
  }>({ isOpen: false, mode: 'remove', itemId: null });

  // ── Helpers ──────────────────────────────────────────────────────────────
  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchWishlist = useCallback(async () => {
    try {
      setLoading(true);
      const { items: fetched, totalItems: total } = await storeService.getWishlist();
      setItems(fetched);
      setTotalItems(total);
    } catch {
      showMessage(
        'error',
        t('operator.commerce.wishlist.fetchError', { defaultValue: 'Failed to load wishlist.' })
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  // ── Add single item to cart ───────────────────────────────────────────────
  const handleAddToCart = async (item: WishlistItem) => {
    if (item.stockStatus === 'out_of_stock') {
      showMessage(
        'error',
        t('operator.commerce.wishlist.outOfStockError', { defaultValue: 'This item is currently out of stock.' })
      );
      return;
    }
    setAddingToCart(prev => new Set(prev).add(item.id));
    try {
      await storeService.addToCart({ productId: item.productId, quantity: 1 });
      showMessage(
        'success',
        t('operator.commerce.wishlist.addedToCart', {
          defaultValue: '{{name}} added to cart.',
          name: item.name,
        })
      );
      window.dispatchEvent(new Event('cartUpdated'));
    } catch {
      showMessage(
        'error',
        t('operator.commerce.wishlist.addToCartError', { defaultValue: 'Failed to add to cart. Please try again.' })
      );
    } finally {
      setAddingToCart(prev => { const s = new Set(prev); s.delete(item.id); return s; });
    }
  };

  // ── Remove single item ────────────────────────────────────────────────────
  const handleRemoveItem = (itemId: string) => {
    setConfirmModal({ isOpen: true, mode: 'remove', itemId });
  };

  const confirmRemove = async () => {
    const itemId = confirmModal.itemId;
    if (!itemId) return;
    setConfirmModal({ isOpen: false, mode: 'remove', itemId: null });
    setRemoving(prev => new Set(prev).add(itemId));
    try {
      await storeService.removeFromWishlist(itemId);
      const updated = (items ?? []).filter(i => i.id !== itemId);
      setItems(updated);
      setTotalItems(updated.length);
      showMessage(
        'success',
        t('operator.commerce.wishlist.removeSuccess', { defaultValue: 'Item removed from wishlist.' })
      );
    } catch {
      showMessage(
        'error',
        t('operator.commerce.wishlist.removeError', { defaultValue: 'Failed to remove item. Please try again.' })
      );
    } finally {
      setRemoving(prev => { const s = new Set(prev); s.delete(itemId); return s; });
    }
  };

  // ── Clear all ─────────────────────────────────────────────────────────────
  const handleClearAll = () => {
    setConfirmModal({ isOpen: true, mode: 'clear', itemId: null });
  };

  const confirmClearAll = async () => {
    setConfirmModal({ isOpen: false, mode: 'clear', itemId: null });
    setClearingAll(true);
    try {
      await storeService.clearWishlist();
      setItems([]);
      setTotalItems(0);
      showMessage(
        'success',
        t('operator.commerce.wishlist.clearSuccess', { defaultValue: 'Wishlist cleared successfully.' })
      );
    } catch {
      showMessage(
        'error',
        t('operator.commerce.wishlist.clearError', { defaultValue: 'Failed to clear wishlist. Please try again.' })
      );
    } finally {
      setClearingAll(false);
    }
  };

  const onModalConfirm = confirmModal.mode === 'clear' ? confirmClearAll : confirmRemove;

  // ── Loading gate ──────────────────────────────────────────────────────────
  if (loading || items === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('operator.commerce.wishlist.loading', { defaultValue: 'Loading wishlist…' })}
          </p>
        </div>
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <>
      <Helmet>
        <title>
          {t('operator.commerce.wishlist.meta.title', { defaultValue: 'Wishlist | KonectX' })}
        </title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* ── Toast ── */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
              }`}
            >
              {message.type === 'success'
                ? <CheckCircle className="w-5 h-5 flex-shrink-0" />
                : <XCircle className="w-5 h-5 flex-shrink-0" />}
              <span className="text-sm font-medium">{message.text}</span>
            </div>
          )}

          {/* ── Page header ── */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/operator/store')}
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-5"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('operator.commerce.common.continueShopping', { defaultValue: 'Continue Shopping' })}
            </button>

            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-2 rounded-xl bg-rose-50 dark:bg-rose-900/20">
                    <Heart className="w-5 h-5 text-rose-500" />
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {t('operator.commerce.wishlist.title', { defaultValue: 'Wishlist' })}
                  </h1>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 ml-12">
                  {t('operator.commerce.wishlist.count', {
                    defaultValue: '{{count}} saved item(s)',
                    count: totalItems,
                  })}
                </p>
              </div>

              {items.length > 0 && (
                <button
                  onClick={handleClearAll}
                  disabled={clearingAll}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                >
                  {clearingAll
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <HeartOff className="w-4 h-4" />}
                  {t('operator.commerce.wishlist.clearAll', { defaultValue: 'Clear All' })}
                </button>
              )}
            </div>
          </div>

          {/* ── Empty state ── */}
          {items.length === 0 ? (
            <div className="bg-white dark:bg-[#333436] rounded-2xl border border-gray-100 dark:border-gray-700/50 p-16 text-center">
              <div className="w-20 h-20 rounded-full bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center mx-auto mb-5">
                <Heart className="w-9 h-9 text-rose-300 dark:text-rose-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                {t('operator.commerce.wishlist.emptyTitle', { defaultValue: 'Nothing saved yet' })}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-xs mx-auto">
                {t('operator.commerce.wishlist.emptyBody', {
                  defaultValue: 'Browse the store and tap the heart icon to save products here.',
                })}
              </p>
              <button
                onClick={() => navigate('/operator/store')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 dark:hover:bg-blue-500 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" />
                {t('operator.commerce.common.startShopping', { defaultValue: 'Browse Store' })}
              </button>
            </div>
          ) : (
            /* ── Items grid ── */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map((item) => {
                const isRemoving   = removing.has(item.id);
                const isAddingCart = addingToCart.has(item.id);
                const outOfStock   = item.stockStatus === 'out_of_stock';

                return (
                  <div
                    key={item.id}
                    className={`group relative bg-white dark:bg-[#333436] rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 ${
                      isRemoving ? 'opacity-60 pointer-events-none' : ''
                    }`}
                  >
                    {/* Remove overlay while deleting */}
                    {isRemoving && (
                      <div className="absolute inset-0 bg-white/60 dark:bg-black/60 flex items-center justify-center z-10 rounded-2xl">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      </div>
                    )}

                    {/* Remove button — visible on hover */}
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={isRemoving}
                      className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all opacity-0 group-hover:opacity-100 shadow-sm"
                      aria-label={t('operator.commerce.common.remove', { defaultValue: 'Remove' })}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>

                    {/* Out of stock badge */}
                    {outOfStock && (
                      <div className="absolute top-3 left-3 z-20 px-2 py-0.5 rounded-full bg-gray-900/80 dark:bg-gray-700/90 backdrop-blur-sm text-white text-xs font-medium">
                        {t('operator.commerce.wishlist.outOfStockBadge', { defaultValue: 'Out of Stock' })}
                      </div>
                    )}

                    {/* Product image */}
                    <div
                      className="h-44 bg-gray-50 dark:bg-[#1a1b1e] flex items-center justify-center overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/operator/store/product/${item.productId}`)}
                    >
                      {item.thumbnailUrl ? (
                        <img
                          src={item.thumbnailUrl}
                          alt={item.name}
                          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'https://placehold.co/200x200/1a1a2e/60a5fa?text=KX';
                          }}
                        />
                      ) : (
                        <Package className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                      )}
                    </div>

                    {/* Product details */}
                    <div className="p-4">
                      <h3
                        className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5 line-clamp-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors leading-snug"
                        onClick={() => navigate(`/operator/store/product/${item.productId}`)}
                      >
                        {item.name}
                      </h3>

                      <StarRating rating={item.rating} />

                      <div className="flex items-center justify-between mt-3 mb-4">
                        <span className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(item.price, item.currency)}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          outOfStock
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
                        }`}>
                          {outOfStock
                            ? t('operator.commerce.wishlist.outOfStock', { defaultValue: 'Out of stock' })
                            : t('operator.commerce.wishlist.inStock', { defaultValue: 'In stock' })}
                        </span>
                      </div>

                      {/* Add to cart */}
                      <button
                        onClick={() => handleAddToCart(item)}
                        disabled={isAddingCart || outOfStock}
                        className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-60 ${
                          outOfStock
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'bg-gray-900 dark:bg-blue-600 text-white hover:bg-gray-800 dark:hover:bg-blue-500'
                        }`}
                      >
                        {isAddingCart
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <ShoppingCart className="w-4 h-4" />}
                        {isAddingCart
                          ? t('operator.commerce.wishlist.addingToCart', { defaultValue: 'Adding…' })
                          : t('operator.commerce.wishlist.addToCart', { defaultValue: 'Add to Cart' })}
                      </button>

                      {/* Saved date — uses API addedAt field */}
                      <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-3">
                        {t('operator.commerce.wishlist.savedOn', {
                          defaultValue: 'Saved {{date}}',
                          date: formatDate(item.addedAt),
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Confirmation modal (shared for remove + clear) ── */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={onModalConfirm}
        title={
          confirmModal.mode === 'clear'
            ? t('operator.commerce.wishlist.confirmClearTitle', { defaultValue: 'Clear Wishlist' })
            : t('operator.commerce.wishlist.confirmRemoveTitle', { defaultValue: 'Remove Item' })
        }
        message={
          confirmModal.mode === 'clear'
            ? t('operator.commerce.wishlist.confirmClearBody', {
                defaultValue: 'Are you sure you want to remove all items from your wishlist?',
              })
            : t('operator.commerce.wishlist.confirmRemoveBody', {
                defaultValue: 'Are you sure you want to remove this item from your wishlist?',
              })
        }
        confirmText={
          confirmModal.mode === 'clear'
            ? t('operator.commerce.wishlist.clearAll', { defaultValue: 'Clear All' })
            : t('operator.commerce.common.remove', { defaultValue: 'Remove' })
        }
        cancelText={t('operator.commerce.common.cancel', { defaultValue: 'Cancel' })}
        variant="danger"
      />
    </>
  );
};

export default Wishlist;